import { z } from 'zod';
import { isValidCpf } from '../utils/cpfValidator';

/**
 * Schema de validação para Serviços
 */
export const serviceSchema = z.object({
  name: z.string()
    .min(3, 'Nome deve ter no mínimo 3 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres'),
  
  description: z.string()
    .max(500, 'Descrição deve ter no máximo 500 caracteres')
    .optional()
    .or(z.literal('')),
  
  category: z.string()
    .max(100, 'Categoria deve ter no máximo 100 caracteres')
    .optional()
    .or(z.literal('')),
  
  duration: z.number()
    .int('Duração deve ser um número inteiro')
    .positive('Duração deve ser positiva')
    .min(5, 'Duração mínima é 5 minutos')
    .max(480, 'Duração máxima é 480 minutos (8 horas)'),
  
  price: z.number()
    .nonnegative('Preço não pode ser negativo')
    .max(999999.99, 'Preço máximo é R$ 999.999,99')
    .optional()
    .or(z.literal(0)),
});

/**
 * Schema de validação para Profissionais
 */
export const professionalSchema = z.object({
  name: z.string()
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(200, 'Nome deve ter no máximo 200 caracteres'),
  
  title: z.string()
    .max(50, 'Título deve ter no máximo 50 caracteres')
    .optional()
    .or(z.literal('')),
  
  specialty: z.string()
    .max(100, 'Especialidade deve ter no máximo 100 caracteres')
    .optional()
    .or(z.literal('')),
  
  registrationNumber: z.string()
    .max(50, 'Número de registro deve ter no máximo 50 caracteres')
    .optional()
    .or(z.literal('')),
  
  phone: z.string()
    .refine(
      (v) => !v || /^\d{10,11}$/.test(v.replace(/\D/g, '')),
      'Telefone inválido. Informe um número com DDD (10 dígitos para fixo ou 11 para celular).'
    )
    .optional()
    .or(z.literal('')),

  email: z.string()
    .email('Email inválido')
    .optional()
    .or(z.literal('')),

  description: z.string()
    .max(500, 'Descrição deve ter no máximo 500 caracteres')
    .optional()
    .or(z.literal('')),

  bio: z.string()
    .max(1000, 'Biografia deve ter no máximo 1000 caracteres')
    .optional()
    .or(z.literal('')),
});

/**
 * Schema de validação para Pessoas
 */
export const personSchema = z.object({
  name: z.string()
    .min(3, 'Nome deve ter no mínimo 3 caracteres')
    .max(200, 'Nome deve ter no máximo 200 caracteres'),

  email: z.string()
    .email('Email inválido')
    .max(200, 'Email deve ter no máximo 200 caracteres')
    .optional()
    .or(z.literal('')),

  phone: z.string()
    .min(1, 'Telefone é obrigatório')
    .refine(
      (v) => /^\d{10,11}$/.test(v.replace(/\D/g, '')),
      'Telefone inválido. Informe um número com DDD (10 dígitos para fixo ou 11 para celular).'
    ),

  cpf: z.string()
    .regex(/^\d{11}$|^\d{3}\.\d{3}\.\d{3}-\d{2}$|^$/, 'CPF inválido (11 dígitos ou formato XXX.XXX.XXX-XX)')
    .refine(
      (v) => !v || isValidCpf(v),
      'CPF inválido (dígitos verificadores)'
    )
    .optional()
    .or(z.literal('')),
  
  birthDate: z.string()
    .optional()
    .or(z.literal('')),
  
  address: z.string()
    .max(500, 'Endereço deve ter no máximo 500 caracteres')
    .optional()
    .or(z.literal('')),

  addressZipCode: z.string()
    .regex(/^\d{8}$|^\d{5}-\d{3}$|^$/, 'CEP inválido (8 dígitos ou formato XXXXX-XXX)')
    .optional()
    .or(z.literal('')),

  addressStreet: z.string()
    .max(200, 'Rua deve ter no máximo 200 caracteres')
    .optional()
    .or(z.literal('')),

  addressNumber: z.string()
    .max(10, 'Número deve ter no máximo 10 caracteres')
    .optional()
    .or(z.literal('')),

  addressComplement: z.string()
    .max(255, 'Complemento deve ter no máximo 255 caracteres')
    .optional()
    .or(z.literal('')),

  addressNeighborhood: z.string()
    .max(100, 'Bairro deve ter no máximo 100 caracteres')
    .optional()
    .or(z.literal('')),

  addressCity: z.string()
    .max(100, 'Cidade deve ter no máximo 100 caracteres')
    .optional()
    .or(z.literal('')),

  addressState: z.string()
    .length(2, 'Estado deve ter 2 letras (UF)')
    .optional()
    .or(z.literal('')),

  notes: z.string()
    .max(1000, 'Observações devem ter no máximo 1000 caracteres')
    .optional()
    .or(z.literal('')),
});

/**
 * Schema de validação para Locais
 */
export const placeSchema = z.object({
  name: z.string()
    .min(3, 'Nome deve ter no mínimo 3 caracteres')
    .max(200, 'Nome deve ter no máximo 200 caracteres'),
  
  description: z.string()
    .max(500, 'Descrição deve ter no máximo 500 caracteres')
    .optional()
    .or(z.literal('')),
  
  email: z.string()
    .email('Email inválido')
    .max(200, 'Email deve ter no máximo 200 caracteres')
    .optional()
    .or(z.literal('')),
  
  phone: z.string()
    .refine(
      (v) => !v || /^\d{10,11}$/.test(v.replace(/\D/g, '')),
      'Telefone inválido. Informe um número com DDD (10 dígitos para fixo ou 11 para celular).'
    )
    .optional()
    .or(z.literal('')),

  website: z.string()
    .url('URL inválida')
    .max(500, 'Website deve ter no máximo 500 caracteres')
    .optional()
    .or(z.literal('')),
  
  addressStreet: z.string()
    .min(3, 'Rua deve ter no mínimo 3 caracteres')
    .max(200, 'Rua deve ter no máximo 200 caracteres')
    .optional()
    .or(z.literal('')),
  
  addressNumber: z.string()
    .max(20, 'Número deve ter no máximo 20 caracteres')
    .optional()
    .or(z.literal('')),
  
  addressComplement: z.string()
    .max(100, 'Complemento deve ter no máximo 100 caracteres')
    .optional()
    .or(z.literal('')),
  
  addressNeighborhood: z.string()
    .max(100, 'Bairro deve ter no máximo 100 caracteres')
    .optional()
    .or(z.literal('')),
  
  addressCity: z.string()
    .min(2, 'Cidade deve ter no mínimo 2 caracteres')
    .max(100, 'Cidade deve ter no máximo 100 caracteres')
    .optional()
    .or(z.literal('')),
  
  addressState: z.string()
    .length(2, 'Estado deve ter 2 letras (UF)')
    .regex(/^[A-Z]{2}$/, 'Estado deve ser a sigla da UF (ex: SP, RJ)')
    .optional()
    .or(z.literal('')),
  
  addressZipCode: z.string()
    .regex(/^\d{8}$|^\d{5}-\d{3}$/, 'CEP inválido (8 dígitos ou formato XXXXX-XXX)')
    .optional()
    .or(z.literal('')),
});

/**
 * Schema de validação para Agendas
 */
export const agendaSchema = z.object({
  placeId: z.number()
    .int('Place ID deve ser um número inteiro')
    .positive('Selecione um local'),
  
  professionalId: z.number()
    .int('Professional ID deve ser um número inteiro')
    .positive('Selecione um profissional'),
  
  dayOfWeek: z.number()
    .int('Dia da semana deve ser um número inteiro')
    .min(0, 'Dia da semana deve ser entre 0 (Domingo) e 6 (Sábado)')
    .max(6, 'Dia da semana deve ser entre 0 (Domingo) e 6 (Sábado)'),
  
  startTime: z.string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Horário inválido (formato HH:MM)'),
  
  endTime: z.string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Horário inválido (formato HH:MM)'),
  
  isActive: z.boolean()
    .optional(),
}).refine((data) => {
  // Validar que endTime é maior que startTime
  if (data.startTime && data.endTime) {
    const [startHour, startMin] = data.startTime.split(':').map(Number);
    const [endHour, endMin] = data.endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return endMinutes > startMinutes;
  }
  return true;
}, {
  message: 'Horário final deve ser maior que horário inicial',
  path: ['endTime'],
});

/**
 * Schema de validação para Métodos de Pagamento
 */
export const paymentSchema = z.object({
  name: z.string()
    .min(3, 'Nome deve ter no mínimo 3 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres'),
  
  description: z.string()
    .max(500, 'Descrição deve ter no máximo 500 caracteres')
    .optional()
    .or(z.literal('')),
  
  type: z.string()
    .max(50, 'Tipo deve ter no máximo 50 caracteres')
    .optional()
    .or(z.literal('')),
});

/**
 * Schema de validação para Páginas Link-in-Bio
 */
export const pageSchema = z.object({
  title: z.string()
    .min(3, 'Título deve ter no mínimo 3 caracteres')
    .max(200, 'Título deve ter no máximo 200 caracteres'),
  
  subtitle: z.string()
    .max(3000, 'Subtítulo deve ter no máximo 3000 caracteres')
    .optional()
    .or(z.literal('')),
  
  slug: z.string()
    .min(3, 'Slug deve ter no mínimo 3 caracteres')
    .max(100, 'Slug deve ter no máximo 100 caracteres')
    .regex(/^[a-z0-9-]+$/, 'Slug pode conter apenas letras minúsculas, números e hífens'),
  
  templateId: z.string()
    .min(1, 'Selecione um template'),
  
  scheduleId: z.string()
    .min(1, 'Selecione uma agenda'),
  
  socialLinks: z.record(z.object({
    url: z.string().url('URL de rede social inválida').optional().or(z.literal('')),
  })).optional(),
  
  genericLinks: z.array(z.object({
    url: z.string().url('URL do link inválida').optional().or(z.literal('')),
    name: z.string().max(100, 'Nome do link muito longo').optional().or(z.literal('')),
  })).optional(),
  
  customStyles: z.object({
    backgroundColor: z.string().optional().or(z.literal('')),
    textColor: z.string().optional().or(z.literal('')),
    fontFamily: z.enum(['Roboto', 'Open Sans', 'Lato', 'Montserrat'], {
      errorMap: () => ({ message: 'Fonte inválida' }),
    }).optional(),
    fontSize: z.enum(['14px', '16px', '18px', '20px'], {
      errorMap: () => ({ message: 'Tamanho de fonte inválido' }),
    }).optional(),
    linkColor: z.string().optional().or(z.literal('')),
    linkBackground: z.string().optional().or(z.literal('')),
  }).optional(),
});

// Exportar todos os schemas
export default {
  serviceSchema,
  professionalSchema,
  personSchema,
  placeSchema,
  agendaSchema,
  paymentSchema,
  pageSchema,
};
